import { FC, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Joyride from 'react-joyride';
import { CSSTransition } from 'react-transition-group';
import RewardsHeader from '../RewardsHeader';
import AchievementActivityFeed from '../AchievementActivityFeed';
import Gamification, { AchievementCard, PointSummary } from '../../services/Gamification';

import { setTourViewed, getTourViewed } from '../../services/TourService';

import AchievementCardList from '../AchievementCardList';
import LoaderAnimation from '../LoaderAnimation';

import { datadogRum } from '@datadog/browser-rum';

//Adding global property to window to solve dependency issue
import './globalFix';

const tourID = 'gamification_tour';

const steps = [
  {
    target: '.gamification_tour_intro',
    content: "Welcome to 'My Rewards', lets take a tour.\n Press 'Skip' at any time to end the tour",
    disableBeacon: true,
  },
  {
    target: '.gamification_tour_bar',
    content: 'Earn points by playing games with your players and by exploring the different features of RESTORE',
    disableBeacon: true,
  },
  {
    target: '.gamification_tour_level',
    content: 'Earn enough points to reach new user levels',
    disableBeacon: true,
  },
  {
    target: '.gamification_tour_1',
    content: 'Unlock achievements by completing goals and earn rewards ',
    disableBeacon: true,
  },
  {
    target: '.gamification_tour_2',
    content: 'Review your progress and see how you compare to your team members',
    disableBeacon: true,
  },
  {
    target: '.gamification_tour_3',
    content: 'Click View All to open the full My Rewards page and see all of your points, levels, and achievements',
    disableBeacon: true,
  },
];

const Overlay = styled.div`
  height: 100%;
  width: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 3;
  opacity: 0;
`;

const Container = styled.div(({ theme }) => {
  const { color } = theme;

  return `
    position: absolute;
    height: 100vh;
    width: 28%;
    min-width: 450px;
    top: 0;
    right: 0;
    background-color: ${color.grayscale[1000]};
    z-index: 1004;
    padding: 24px;
    transform: translateX(100%);
    display: flex;
    flex-flow: column nowrap;
    overflow-y: scroll;
  `;
});

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100vw;
  width: 100vw;
  z-index: 2;
  overflow-x: hidden;
  display: none;

  &.visible {
    display: block;
  }

  &.transition {
    &-enter {
      ${Overlay} {
        opacity: 0;
      }

      ${Container} = > {
        transform: translateX(100%);
      }

      &-active {
        ${Overlay} {
          opacity: 1;
          transition: opacity ease-in-out 200ms;
        }

        ${Container} {
          transform: translateX(0);
          transition: transform ease-in-out 200ms;
        }
      }

      &-done {
        ${Overlay} {
          opacity: 1;
        }

        ${Container} {
          transform: translateX(0);
        }
      }
    }

    &-exit {
      ${Overlay} {
        opacity: 1;
      }

      & > ${Container} {
        transform: translateX(0);
      }

      &-active {
        ${Overlay} {
          opacity: 0;
          transition: opacity ease-in-out 200ms;
        }

        ${Container} {
          transform: translateX(100%);
          transition: transform ease-in-out 200ms;
        }
      }

      &-done {
        & > ${Overlay} {
          opacity: 0;
        }

        & > ${Container} {
          transform: translateX(100%);
        }
      }
    }
  }
`;

const CloseButton = styled.button(({ theme }) => {
  return `
    font-size: ${theme.type.size.large};
    font-weight: ${theme.type.weight.bold};
    cursor: pointer;
    border: none;
    color: ${theme.color.primary.regular};
    background: none;
    position: absolute;
    right: 20px;
    top: 20px;
    z-index: 2;
    user-select: none;
  `;
});

export interface IAchievementDrawer {
  open: boolean;
  closeCallback: () => void;
}

const AchievementDrawer: FC<IAchievementDrawer> = ({ open, closeCallback }) => {
  const history = useHistory();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [displayBlock, setDisplayBlock] = useState<boolean>(false);
  const [levelInfo, setLevelInfo] = useState<PointSummary>({
    title: '',
    progress: 0,
    points: 0,
    threshold: 0,
    level: -1,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [achievementInfo, setAchievementInfo] = useState<AchievementCard[]>([]);
  const [tourSeen, setTourSeen] = useState<boolean>(false);

  const [tourSkipped, setTourSkipped] = useState(false);
  const [tourFinished, setTourFinished] = useState(false);

  // const [previousFocus, setPreviousFocus] = useState<Element | null>();

  const htmlElement = document.getElementsByTagName('html')[0];

  const getLevelSummary = async () => {
    const response = await Gamification.getPointSummary();

    if (response !== null) setLevelInfo(response);
  };

  const getAchievementInfo = async () => {
    const progress = await Gamification.getAchievementProgress();
    setAchievementInfo(progress);
  };

  const handleClose = () => {
    htmlElement.classList.remove('scrollLock');
    setDisplayBlock(false);
    closeCallback();
    // SET FOCUS TO PREVIOUS ELEMENT HERE
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (!drawerRef?.current?.contains(e.target as Node)) {
      handleClose();
    }
  };

  const handleEscapePress = (e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      handleClose();
    }
  };

  const handleOpen = () => {
    const levelSummary = getLevelSummary();
    const achievementInfo = getAchievementInfo();

    htmlElement.classList.add('scrollLock');
    setDisplayBlock(true);
    setLoading(true);

    Promise.all([levelSummary, achievementInfo]).then(() => {
      setLoading(false);
    });

    const tourViewed = getTourViewed(tourID);
    setTourSeen(tourViewed);

    if (!tourViewed) {
      setTourViewed(tourID);
    } else {
      document.addEventListener('mouseup', handleOutsideClick);
      document.addEventListener('keyup', handleEscapePress);
    }

    // setPreviousFocus(document.activeElement);
    // SET FOCUS TO DIALOG ELEMENT HERE
  };

  const viewAllAction = () => {
    handleClose();
    datadogRum.addAction('viewAllClicked', {});
    history.push('/rewards');
  };

  type JoyrideState = {
    action: string;
    index: number;
    size: number;
  };

  const joyrideStateChange = async (newState: JoyrideState) => {
    const action = newState.action;

    if (action === 'next' && newState.index === newState.size - 1) {
      if (!tourFinished) {
        await setTourFinished(true);
        datadogRum.addAction('gamificationTourFinished', {});
      }
    }

    if (action === 'skip') {
      if (!tourSkipped) {
        datadogRum.addAction('gamificationTourSkip', {});
      }

      await setTourSkipped(true);
    }

    if (action === 'reset') {
      document.addEventListener('mouseup', handleOutsideClick);
      document.addEventListener('keyup', handleEscapePress);
    }
  };

  return (
    <CSSTransition
      in={open}
      timeout={200}
      classNames="transition"
      onEnter={handleOpen}
      onExited={() => setDisplayBlock(false)}
    >
      <Wrapper className={displayBlock ? 'visible' : ''}>
        <Overlay>
          <Container ref={drawerRef}>
            <CloseButton onClick={handleClose}>X</CloseButton>

            {loading ? (
              <LoaderAnimation />
            ) : (
              <>
                {!tourSeen && (
                  <Joyride
                    steps={steps}
                    continuous={true}
                    showProgress={true}
                    showSkipButton={true}
                    disableOverlay={false}
                    disableScrolling={true}
                    callback={joyrideStateChange}
                  />
                )}
                <div>
                  {levelInfo.level !== -1 && <RewardsHeader {...levelInfo} compact={true} hideStar={true} />}
                  {achievementInfo?.length > 0 && (
                    <AchievementCardList
                      achievements={achievementInfo}
                      displayCount={2}
                      viewAllAction={viewAllAction}
                    />
                  )}

                  <section>
                    <AchievementActivityFeed viewAllAction={viewAllAction} />
                  </section>
                </div>
              </>
            )}
          </Container>
        </Overlay>
      </Wrapper>
    </CSSTransition>
  );
};

export default AchievementDrawer;
